import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { useAppModule } from '../features/appContext.ts'
import { mainGreetingId } from '../features/main/mainGreeting.ts'
import { usePromise } from '../shared/lib/hooks.ts'
import { cn } from '../shared/lib/utils.ts'
import { urls } from '../shared/urls.ts'
import { Icon, IconName } from '../shared/ui/icon/icon.tsx'
import { AppMessageKeys } from '../shared/translations/messages.ts'

export function MainPage() {
  const intl = useIntl()
  const appModule = useAppModule()
  const account = usePromise(() => appModule.chatsApi.getAccount())
  const loadedName =
    account !== 'loading' && account !== 'error' ? account?.name : ''
  const name = loadedName ? loadedName : intl.formatMessage({ id: 'Friend' })

  return (
    <div className="flex w-full flex-col gap-20 px-16 pb-100 pt-20 xl:gap-24 xl:px-0 xl:pb-40 xl:pt-28 2xl:gap-40 2xl:pb-56 2xl:pt-44">
      <header className="flex flex-col gap-8 2xl:gap-16">
        <h1 className="text-22 font-bold leading-tight text-black xl:text-30 2xl:text-44">
          <FormattedMessage id={mainGreetingId()} values={{ name }} />
        </h1>
        <p className="text-14 text-gray4 xl:text-16 2xl:text-22">
          <FormattedMessage id="main.greeting.subtitle" />
        </p>
      </header>

      <section className="flex flex-col gap-12 2xl:gap-20" aria-label="modules">
        <Link
          to={urls.freeTalk}
          className="flex items-start gap-16 rounded-18 bg-violetd p-18 text-white transition-transform hover:-translate-y-2 hover:shadow-container xl:p-22 2xl:gap-24 2xl:rounded-32 2xl:p-36"
        >
          <span className="flex size-52 shrink-0 items-center justify-center rounded-16 bg-violetd-light text-purple4 2xl:size-80 2xl:rounded-24">
            <Icon iconName="message" size="large" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col gap-4 2xl:gap-10">
            <span className="flex flex-wrap items-center gap-10 2xl:gap-14">
              <span className="text-18 font-bold xl:text-20 2xl:text-28">
                <FormattedMessage id="main.freeTalk.title" />
              </span>
              <span className="rounded-full bg-orange5 px-10 py-4 text-10 font-extrabold uppercase tracking-wide text-white 2xl:px-14 2xl:py-6 2xl:text-14">
                <FormattedMessage id="main.freeTalk.badge" />
              </span>
            </span>
            <span className="text-14 leading-snug text-coolgray450 2xl:text-18">
              <FormattedMessage id="main.freeTalk.subtitle" />
            </span>
          </span>
          <span className="flex h-40 shrink-0 items-center gap-8 rounded-full bg-white px-16 text-14 font-bold text-violetd 2xl:h-56 2xl:px-24 2xl:text-18">
            <Icon iconName="mic" size="small" />
            <FormattedMessage id="main.freeTalk.cta" />
          </span>
        </Link>
        <ModuleLink
          to={urls.learningProfile()}
          icon="navStar"
          iconTone="blue"
          titleId="navbar.lessons"
          textId="main.lessons.subtitle"
        />
        <ModuleLink
          to={urls.practice}
          icon="book"
          iconTone="warm"
          titleId="navbar.vocabPlus"
          textId="main.vocab.subtitle"
        />
      </section>
    </div>
  )
}

function ModuleLink(props: {
  to: string
  icon: IconName
  iconTone: 'blue' | 'warm'
  titleId: AppMessageKeys
  textId: AppMessageKeys
}) {
  return (
    <Link
      to={props.to}
      className="flex items-center gap-16 rounded-18 bg-white p-18 shadow-button transition-transform hover:-translate-y-2 hover:shadow-container xl:p-22 2xl:gap-24 2xl:rounded-32 2xl:p-36"
    >
      <span
        className={cn(
          'flex size-52 shrink-0 items-center justify-center rounded-16 2xl:size-80 2xl:rounded-24',
          props.iconTone === 'blue'
            ? 'bg-second-light text-main'
            : 'bg-warning text-warning-darker',
        )}
      >
        <Icon iconName={props.icon} size="large" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-4 2xl:gap-10">
        <span className="text-18 font-bold text-black xl:text-20 2xl:text-28">
          <FormattedMessage id={props.titleId} />
        </span>
        <span className="text-14 leading-snug text-gray4 2xl:text-18">
          <FormattedMessage id={props.textId} />
        </span>
      </span>
      <span className="flex size-40 shrink-0 items-center justify-center rounded-full bg-gray-light text-black 2xl:size-56">
        <Icon iconName="arrowRight" size="small" />
      </span>
    </Link>
  )
}
